import { useEffect, useId, useState } from "react";
import { InlineSpinner } from "./InlineSpinner";

/** 노래·뮤직비디오 제목 — 1~40자(서버·027 CHECK 와 같다). */
export const TITLE_MAX_CHARS = 40;

/**
 * 제목 고치기 칸(2026-10-12 용인신촌중 수업). 노래 제목은 가사 첫 줄로, 뮤직비디오 제목은
 * 줄거리로 자동으로 정해졌고 학생이 고칠 길이 없었다.
 * `alwaysOpen` 이 아니면 「제목 고치기」 버튼을 눌러야 칸이 열린다(곡 카드처럼 좁은 자리).
 */
export function TitleEditor({
  value,
  label,
  onSave,
  alwaysOpen = false,
  resetHint,
}: {
  value: string;
  label: string;
  onSave: (title: string) => Promise<void>;
  alwaysOpen?: boolean;
  /**
   * 있으면 칸을 비워 저장할 수 있다 = 직접 고친 제목을 지우고 자동 제목으로 돌아간다(뮤직비디오).
   * 없으면 빈 제목은 저장하지 않는다(노래 — 제목이 꼭 있어야 한다).
   */
  resetHint?: string | undefined;
}) {
  const inputId = useId();
  const [open, setOpen] = useState(alwaysOpen);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // 서버가 돌려준 제목(또는 다시 읽은 제목)으로 칸을 맞춘다. 🔑 저장할 때마다도 맞춘다 — 되돌린 제목이
  // 저장 전과 같은 글자면 `value` 가 안 바뀌어 빈 칸이 남았다(Codex 재검수 2026-10-12).
  const [savedTick, setSavedTick] = useState(0);
  useEffect(() => {
    setDraft(value);
  }, [value, savedTick]);

  useEffect(() => {
    if (!saved) return;
    const timer = window.setTimeout(() => setSaved(false), 2_000);
    return () => window.clearTimeout(timer);
  }, [saved]);

  if (!open) {
    return (
      <button className="button-ghost title-editor-open" type="button" onClick={() => setOpen(true)}>
        제목 고치기
      </button>
    );
  }

  const trimmed = draft.trim();
  const canSave = (trimmed !== "" || resetHint !== undefined) && trimmed !== value;
  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await onSave(trimmed);
      setSaved(true);
      setSavedTick((tick) => tick + 1);
      if (!alwaysOpen) setOpen(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "제목을 저장하지 못했어요.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      className="title-editor"
      onSubmit={(event) => {
        event.preventDefault();
        if (canSave) void save();
      }}
    >
      <label htmlFor={inputId}>{label}</label>
      <div className="title-editor-row">
        <input
          id={inputId}
          value={draft}
          maxLength={TITLE_MAX_CHARS}
          onChange={(event) => setDraft(event.target.value)}
          disabled={saving}
          aria-invalid={Boolean(error)}
          // 「제목 고치기」 를 눌러 연 칸이면 초점을 칸으로 옮긴다 — 누른 버튼이 사라져 초점을 잃는다.
          autoFocus={!alwaysOpen}
        />
        <button className="button-secondary" type="submit" disabled={saving || !canSave}>
          {saving ? <InlineSpinner label="저장 중..." /> : saved ? "저장했어요 ✓" : "저장"}
        </button>
        {!alwaysOpen ? (
          <button className="button-ghost" type="button" disabled={saving} onClick={() => { setDraft(value); setError(null); setOpen(false); }}>
            취소
          </button>
        ) : null}
      </div>
      {resetHint && !trimmed ? <p className="title-editor-hint">{resetHint}</p> : null}
      {error ? <p className="title-editor-error" role="alert">{error}</p> : null}
    </form>
  );
}
